import React, { useRef, useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';
import { X } from 'lucide-react';

const CCTVStream = ({ isOpen, onClose }) => {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const { language } = useLanguage();
    const t = useTranslation(language);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [isRecording, setIsRecording] = useState(false);

    // Update time every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    // Start camera when modal opens
    useEffect(() => {
        if (isOpen) {
            startCamera();
            // Simulate recording animation
            const recordingTimer = setInterval(() => {
                setIsRecording(prev => !prev);
            }, 1000);

            return () => {
                clearInterval(recordingTimer);
                stopCamera();
            };
        } else {
            stopCamera();
        }
    }, [isOpen]);

    const startCamera = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                    facingMode: 'user'
                },
                audio: false
            });

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                streamRef.current = stream;
            }
            setIsLoading(false);
        } catch (err) {
            console.error('Error accessing camera:', err);
            setError('CAMERA ACCESS DENIED');
            setIsLoading(false);
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    const handleClose = () => {
        stopCamera();
        onClose();
    };

    const formatTime = (date) => {
        return date.toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm">
            {/* Modal Container */}
            <div className="relative w-full max-w-5xl mx-4 bg-black border-2 border-green-500/30 shadow-2xl overflow-hidden">
                {/* CCTV Stream Area */}
                <div className="relative aspect-video bg-black overflow-hidden">
                    {/* Video Stream */}
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ filter: 'contrast(1.1) brightness(0.9)' }}
                    />

                    {/* CCTV Overlay */}
                    <div className="absolute inset-0 pointer-events-none">
                        {/* Scanlines effect */}
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-green-500/5 to-transparent animate-scan"
                            style={{
                                backgroundSize: '100% 4px',
                                backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(34, 197, 94, 0.03) 2px, rgba(34, 197, 94, 0.03) 4px)'
                            }}
                        />

                        {/* Top bar with info */}
                        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-4">
                            <div className="flex items-start justify-between">
                                {/* Left side - Camera info */}
                                <div className="space-y-1">
                                    <div className="flex items-center space-x-2">
                                        {/* Recording indicator */}
                                        <div className={`w-3 h-3 rounded-full bg-red-500 transition-opacity duration-300 ${isRecording ? 'opacity-100' : 'opacity-30'}`} />
                                        <span className="text-red-500 text-sm font-mono font-bold tracking-wider">{t('rec')}</span>
                                    </div>
                                    <div className="text-green-400 text-xs font-mono tracking-wider">
                                        {t('cam_info')}
                                    </div>
                                </div>

                                {/* Right side - Date and time */}
                                <div className="text-right space-y-1">
                                    <div className="text-green-400 text-lg font-mono font-bold tracking-wider">
                                        {formatTime(currentTime)}
                                    </div>
                                    <div className="text-green-400 text-xs font-mono tracking-wider">
                                        {formatDate(currentTime)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Corner brackets */}
                        <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-green-500/50" />
                        <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-green-500/50" />
                        <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-green-500/50" />
                        <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-green-500/50" />

                        {/* Center crosshair */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                            <div className="relative w-16 h-16">
                                <div className="absolute top-0 left-1/2 w-0.5 h-4 bg-green-500/30 -translate-x-1/2" />
                                <div className="absolute bottom-0 left-1/2 w-0.5 h-4 bg-green-500/30 -translate-x-1/2" />
                                <div className="absolute left-0 top-1/2 w-4 h-0.5 bg-green-500/30 -translate-y-1/2" />
                                <div className="absolute right-0 top-1/2 w-4 h-0.5 bg-green-500/30 -translate-y-1/2" />
                                <div className="absolute inset-0 border border-green-500/20 rounded-full" />
                            </div>
                        </div>

                        {/* Bottom bar with system info */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                            <div className="flex items-end justify-between text-xs font-mono text-green-400/60">
                                <div className="space-y-1">
                                    <div>{t('resolution')}</div>
                                    <div>{t('fps')}</div>
                                </div>
                                <div className="space-y-1 text-right">
                                    <div>{t('system_active')}</div>
                                    <div>{t('motion_detect')}</div>
                                </div>
                            </div>
                        </div>

                        {/* Grid overlay */}
                        <div className="absolute inset-0">
                            <div className="w-full h-full grid grid-cols-3 grid-rows-3">
                                {[...Array(9)].map((_, i) => (
                                    <div key={i} className="border border-green-500/10"></div>
                                ))}
                            </div>
                        </div>

                        {/* Vignette effect */}
                        <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/50" />
                    </div>

                    {/* Loading State */}
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/90">
                            <div className="text-center">
                                <div className="w-16 h-16 border-4 border-green-500/20 border-t-green-500 rounded-full animate-spin mx-auto mb-4"></div>
                                <p className="text-green-400 font-mono text-sm tracking-wider">{t('initializing_camera')}</p>
                            </div>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/95">
                            <div className="text-center px-6">
                                <div className="w-20 h-20 rounded-full border-4 border-red-500/30 flex items-center justify-center mx-auto mb-4">
                                    <X className="w-10 h-10 text-red-500" />
                                </div>
                                <p className="text-red-500 font-mono font-bold text-lg mb-2">{t('system_error')}</p>
                                <p className="text-red-400/80 font-mono text-sm tracking-wider">{t('camera_access_denied')}</p>
                                <button
                                    onClick={startCamera}
                                    className="mt-6 px-6 py-2 bg-red-500/20 border border-red-500/50 hover:bg-red-500/30 rounded text-red-400 font-mono text-sm transition-all duration-300"
                                >
                                    {t('retry_connection')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Close Button */}
                <button
                    onClick={handleClose}
                    className="absolute top-6 right-6 z-10 p-2 rounded bg-black/50 border border-green-500/30 hover:bg-black/70 hover:border-green-500/50 transition-all duration-300 group"
                >
                    <X className="w-5 h-5 text-green-400/60 group-hover:text-green-400 transition-colors" />
                </button>
            </div>

            {/* Custom animations */}
            <style jsx>{`
            @keyframes scan {
                0% {
                    transform: translateY(-100%);
                }
                100% {
                    transform: translateY(100%);
                }
            }

            .animate-scan {
                animation: scan 8s linear infinite;
            }

            .bg-gradient-radial {
                background: radial-gradient(circle, transparent 0%, transparent 50%, rgba(0,0,0,0.5) 100%);
            }
        `}</style>
        </div>
    );
};

export default CCTVStream;
